import { useEffect, useState } from "react";
import { useRobot } from "@/context/RobotContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Stop,
  PaperPlaneRight,
  Plus,
  Trash,
  Lightning,
} from "@phosphor-icons/react";

const PRESETS = [
  { label: "Forward", payload: "FWD", icon: ArrowUp, testid: "preset-command-forward", col: "col-start-2" },
  { label: "Left", payload: "LEFT", icon: ArrowLeft, testid: "preset-command-left", col: "col-start-1 row-start-2" },
  { label: "Stop", payload: "STOP", icon: Stop, testid: "preset-command-stop", col: "col-start-2 row-start-2", danger: true },
  { label: "Right", payload: "RIGHT", icon: ArrowRight, testid: "preset-command-right", col: "col-start-3 row-start-2" },
  { label: "Back", payload: "BACK", icon: ArrowDown, testid: "preset-command-back", col: "col-start-2 row-start-3" },
];

export default function CommandCenter() {
  const { sendCommand } = useRobot();
  const [nlCommand, setNlCommand] = useState("");
  const [saved, setSaved] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [newPayload, setNewPayload] = useState("");
  const [adding, setAdding] = useState(false);

  const loadSaved = async () => {
    try {
      const res = await api.get("/commands");
      setSaved(res.data);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    loadSaved();
  }, []);

  const fireNatural = async (e) => {
    e.preventDefault();
    if (!nlCommand.trim()) return;
    await sendCommand(nlCommand.trim(), `NL: "${nlCommand.trim()}"`);
    setNlCommand("");
  };

  const saveCommand = async () => {
    if (!newLabel.trim() || !newPayload.trim()) return;
    try {
      const res = await api.post("/commands", { label: newLabel.trim(), payload: newPayload.trim() });
      setSaved((prev) => [res.data, ...prev]);
      setNewLabel("");
      setNewPayload("");
      setAdding(false);
      toast.success("Command saved");
    } catch (e) {
      toast.error("Could not save command");
    }
  };

  const removeCommand = async (id) => {
    try {
      await api.delete(`/commands/${id}`);
      setSaved((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      /* ignore */
    }
  };

  return (
    <div className="border border-[#272A35] bg-[#111318]" data-testid="command-center">
      <div className="border-b border-[#272A35] px-4 py-3">
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#94A3B8] flex items-center gap-2">
          <Lightning size={16} weight="fill" className="text-[#FF5A00]" /> Command Center
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
        {/* D-pad presets */}
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#475569] mb-3">Preset Drive</p>
          <div className="grid grid-cols-3 grid-rows-3 gap-2 max-w-[240px]">
            {PRESETS.map((p) => {
              const Icon = p.icon;
              return (
                <button
                  key={p.label}
                  data-testid={p.testid}
                  onClick={() => sendCommand(p.payload, p.label)}
                  className={`${p.col} aspect-square flex flex-col items-center justify-center gap-1 border transition-all active:scale-95 active:bg-[#FF5A00] active:text-[#0A0B0E] ${
                    p.danger
                      ? "border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-[#0A0B0E]"
                      : "border-[#272A35] text-[#F8FAFC] hover:border-[#94A3B8] hover:bg-[#1A1D24]"
                  }`}
                >
                  <Icon size={22} weight="bold" />
                  <span className="text-[9px] uppercase tracking-widest">{p.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Natural language + saved lib */}
        <div className="space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#475569] mb-2">Natural Language / Vision</p>
            <form onSubmit={fireNatural} className="flex items-stretch border border-[#272A35] focus-within:border-[#FF5A00] transition-colors">
              <span className="grid place-items-center px-3 text-[#FF5A00] font-bold">{">_"}</span>
              <input
                data-testid="custom-command-input"
                value={nlCommand}
                onChange={(e) => setNlCommand(e.target.value)}
                placeholder="go to the door"
                className="flex-1 bg-transparent py-3 font-mono focus:outline-none placeholder:text-[#475569]"
              />
              <button
                type="submit"
                data-testid="custom-command-submit"
                className="px-4 bg-[#FF5A00] text-[#0A0B0E] hover:bg-[#E04F00] transition-colors active:scale-95"
              >
                <PaperPlaneRight size={18} weight="bold" />
              </button>
            </form>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-widest text-[#475569]">Saved Command Library</p>
              <button
                data-testid="add-command-btn"
                onClick={() => setAdding((v) => !v)}
                className="text-[#FF5A00] hover:text-[#E04F00] transition-colors"
              >
                <Plus size={16} weight="bold" />
              </button>
            </div>

            {adding && (
              <div className="flex gap-2 mb-3 fade-up" data-testid="add-command-form">
                <input
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Label"
                  data-testid="new-command-label"
                  className="flex-1 min-w-0 bg-[#0A0B0E] border border-[#272A35] px-2 py-2 text-xs font-mono focus:outline-none focus:border-[#FF5A00]"
                />
                <input
                  value={newPayload}
                  onChange={(e) => setNewPayload(e.target.value)}
                  placeholder="Payload"
                  data-testid="new-command-payload"
                  className="flex-1 min-w-0 bg-[#0A0B0E] border border-[#272A35] px-2 py-2 text-xs font-mono focus:outline-none focus:border-[#FF5A00]"
                />
                <button
                  onClick={saveCommand}
                  data-testid="save-command-btn"
                  className="px-3 bg-[#FF5A00] text-[#0A0B0E] text-xs font-bold uppercase active:scale-95"
                >
                  Save
                </button>
              </div>
            )}

            <div className="flex flex-wrap gap-2" data-testid="saved-commands-list">
              {saved.length === 0 ? (
                <p className="text-[11px] text-[#475569]">No saved commands yet.</p>
              ) : (
                saved.map((c) => (
                  <div
                    key={c.id}
                    className="group flex items-center border border-[#272A35] hover:border-[#94A3B8] transition-colors"
                  >
                    <button
                      data-testid={`saved-command-${c.id}`}
                      onClick={() => sendCommand(c.payload, c.label)}
                      className="px-3 py-2 text-xs font-mono hover:bg-[#1A1D24] active:bg-[#FF5A00] active:text-[#0A0B0E] transition-colors"
                    >
                      {c.label}
                    </button>
                    <button
                      onClick={() => removeCommand(c.id)}
                      className="px-2 py-2 text-[#475569] hover:text-[#FF3B30]"
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
